/**
 * 列表（明细表）数据集字段绑定去重 —— 策略 A。
 *
 * 背景：从 Excel 模板生成的报表 IR 中，常见一种膨胀现象——模板表头下方的每一行数据
 * 都各自重复绑定了一份完整的数据集字段（如 EMPLOYEE_ID/EMPLOYEE_NAME/... 在 row 3~41
 * 每行各写一遍）。但 BaskReport 引擎的明细表语义是：**只需列表的第一行（锚行）绑定
 * 数据集字段并标记 expand:"down"，其余行由引擎按数据展开时自动填充**，无需在 IR 中
 * 逐行声明。
 *
 * 本模块对「已绑定字段的 IR」做后处理去重，把逐行重复的 dataset 绑定压成单锚行。
 * 判定完全依赖「数据行真的填了相同字段」（纯字段比对，**不使用任何样式信号**），
 * 因此对套打单据等带框线但非列表区域零误判。
 *
 * 设计要点：
 *  - 只对明细（aggregate!=='group' 且无条件关联 conditions 为空）的数据集绑定生效；
 *    分组列（group）、主子表条件列（conditions）一律保留，避免破坏分组/主子表语义。
 *  - 「列表区域」= 同一数据集下，多个列在同一批连续行里都出现了完全一致（dataset+field）的绑定。
 *    单列的孤行重复不算列表，必须多列共同构成连续行区间。
 *  - 去重时仅删除「同一 (dataset,field,col) 在锚行之外的重复绑定」；
 *    若某重复行格同时还带 style/文本等非 dataset 内容，则保留该格、仅清空其 dataset content，
 *    不动版式跨度/样式。
 *  - 去重后裁剪列表区域内的「空白重复行」：区域内锚行之外的、整行无任何有效内容
 *    （含仅剩样式格的空行）的行直接删除，rows / rowCount / cells 同步收缩、后续行号上移。
 *    因为列表的其余行由引擎按锚行 expand:"down" 展开生成，模板里重复的数据行无需在 IR
 *    中保留占位，否则会在渲染时留下一大片空行。行内仍含文本/分组/主子表条件等内容的行、
 *    被 rowspan 合并覆盖的行一律保留，区域外的手工留白行不受影响。
 */

import type { CellContent, CellIR, ReportIR } from '../ir/types.js';

/** dataset 单元格内容的类型（从 CellContent 联合类型中提取）。 */
type DatasetContent = Extract<CellContent, { kind: 'dataset' }>;

/** 安全取单元格 dataset 字段名（非 dataset 内容返回空串）。 */
function getField(content: CellContent | undefined): string {
  return content && content.kind === 'dataset' ? content.field : '';
}

/**
 * 判定单元格内容是否为「有效内容」（决定所在行是否可被裁剪）。
 * 空文本（含 trim 后为空）与无内容视为空白；dataset / expression 等一律视为有效。
 */
function hasEffectiveContent(content: CellContent | undefined): boolean {
  if (!content) return false;
  if (content.kind === 'text') return content.value.trim() !== '';
  return true;
}

/** 单元格坐标名（A1 风格）随行号上移而同步更新，例如 A3 → A2；无法解析时原样返回。 */
function shiftCellName(name: string, newRow: number): string {
  const m = name.match(/^([A-Z]+)(\d+)$/);
  return m ? `${m[1]}${newRow}` : name;
}

/**
 * 判定某个 dataset 内容是否属于「可去重的明细绑定」（排除分组与主子表关联）。
 * 明细语义 = aggregate 为 undefined / 'none' / 'select'（见 ir/types.ts：none 序列化归并 select）。
 * 排除 group / conditiongroup / expressiongroup 及各类聚合（sum/avg/...）与带 conditions 的主子表关联列。
 */
const DETAIL_AGGREGATES = new Set(['none', 'select', undefined]);
function isDetailBinding(content: CellContent | undefined): content is DatasetContent {
  if (!content || content.kind !== 'dataset') return false;
  if (!DETAIL_AGGREGATES.has(content.aggregate as string | undefined)) return false;
  if (content.conditions && content.conditions.length > 0) return false;
  return true;
}

/**
 * 对报表 IR 做列表字段绑定去重。返回新的 IR（不修改入参）。
 *
 * @returns 去重后的 IR；同时 `removedCells` / `listRegions` 通过（可选）统计对象返回诊断信息。
 */
export interface DedupeStats {
  /** 被删除（或清空 content）的重复绑定单元格数。 */
  removedBindings: number;
  /** 识别到的列表区域数（每个数据集 × 每个连续行区间算一个）。 */
  listRegions: number;
  /** 去重前/后的 dataset 绑定单元格总数。 */
  beforeBindings: number;
  afterBindings: number;
  /** 被裁剪删除的列表空白行数（rowCount 减少量）。 */
  removedRows: number;
}

export function dedupeListBindings(ir: ReportIR, stats?: DedupeStats): ReportIR {
  const cells = ir.cells;
  const beforeBindings = cells.filter((c) => isDetailBinding(c.content)).length;

  // 1. 按 (dataset, col) 分组「所有」数据集绑定单元格（含 group / conditions，
  //    用于判定列表区域；但去重时只动明细绑定）。
  const byDatasetCol = new Map<string, CellIR[]>();
  for (const c of cells) {
    if (!c.content || c.content.kind !== 'dataset') continue;
    const key = `${c.content.dataset}@${c.col}`;
    const arr = byDatasetCol.get(key);
    if (arr) arr.push(c);
    else byDatasetCol.set(key, [c]);
  }

  // 2. 对每个 (dataset,col)，提取「连续行 + field 恒定」的运行区间（ColumnRun）。
  //    注意：即使该列是 group/conditions，也会参与区域判定，只是后续不去重。
  interface ColumnRun {
    dataset: string;
    col: number;
    field: string;
    rows: number[]; // 升序行号
  }
  const runs: ColumnRun[] = [];
  for (const [key, arr] of byDatasetCol) {
    const [dataset, colStr] = key.split('@') as [string, string];
    const col = Number(colStr);
    const sorted = [...arr].sort((a, b) => a.row - b.row);
    let i = 0;
    while (i < sorted.length) {
      const ci = sorted[i]!;
      const field = getField(ci.content);
      let j = i;
      // 向后扩展连续行且 field 一致
      while (j + 1 < sorted.length) {
        const cj = sorted[j]!;
        const cj1 = sorted[j + 1]!;
        if (cj1.row !== cj.row + 1) break;
        const f1 = getField(cj1.content);
        if (f1 !== field) break;
        j++;
      }
      if (j - i + 1 >= 2) {
        runs.push({
          dataset,
          col,
          field,
          rows: sorted.slice(i, j + 1).map((c) => c.row),
        });
      }
      i = j + 1;
    }
  }

  // 3. 找「列表区域」：同一数据集下，多个列共享同一连续行区间 [start,end]。
  //    关键：区域判定基于「所有列」（含 group），避免分组列「连累」同区域明细列不去重。
  const datasetRuns = new Map<string, ColumnRun[]>();
  for (const r of runs) {
    const arr = datasetRuns.get(r.dataset);
    if (arr) arr.push(r);
    else datasetRuns.set(r.dataset, [r]);
  }

  // 标记需删除的单元格：key = `row@col`
  const toRemove = new Set<string>();
  let listRegions = 0;
  // 识别到的列表区域（行区间，锚行=start），供裁剪空白重复行使用
  const listRegionsInfo: Array<{ start: number; end: number }> = [];

  for (const [dataset, colRuns] of datasetRuns) {
    const intervals: Array<{ start: number; end: number }> = colRuns.map((r) => ({
      start: r.rows[0]!,
      end: r.rows[r.rows.length - 1]!,
    }));
    // 合并重叠区间（重叠 ≥ 1 行即合并），确保整片列表一起处理
    intervals.sort((a, b) => a.start - b.start);
    const merged: Array<{ start: number; end: number }> = [];
    for (const iv of intervals) {
      const last = merged[merged.length - 1];
      if (last && iv.start <= last.end + 1) {
        last.end = Math.max(last.end, iv.end);
      } else {
        merged.push({ ...iv });
      }
    }

    for (const region of merged) {
      // 该区域内参与绑定的列数（任意 dataset 绑定，含 group）
      const colsInRegion = new Set<number>();
      for (const r of colRuns) {
        if (r.rows.some((row) => row >= region.start && row <= region.end)) colsInRegion.add(r.col);
      }
      if (colsInRegion.size < 2) continue; // 单列不足以构成列表，跳过（保守）

      listRegions++;
      listRegionsInfo.push({ ...region });
      // 锚行 = 区域内最小行号
      const anchorRow = region.start;
      // 仅对「明细绑定」标记删除（group/conditions 保留）
      for (const r of colRuns) {
        for (const row of r.rows) {
          if (row === anchorRow) continue;
          if (row < region.start || row > region.end) continue;
          const cell = cells.find((c) => c.row === row && c.col === r.col);
          if (cell && isDetailBinding(cell.content)) toRemove.add(`${row}@${r.col}`);
        }
      }
    }
  }

  // 4. 应用删除/清空（保留 style/文本等版式信息）
  let removedBindings = 0;
  const newCells: CellIR[] = [];
  for (const c of cells) {
    const hit = toRemove.has(`${c.row}@${c.col}`);
    if (hit && isDetailBinding(c.content)) {
      removedBindings++;
      // 若该格还有其他版式信息则保留并清空 content，否则整格删除
      const hasLayout =
        c.style ||
        c.rowspan ||
        c.colspan ||
        c.name ||
        c.left ||
        c.top ||
        c.expand ||
        c.visible !== undefined ||
        c.forcePaging ||
        c.forcePagingBefore;
      if (hasLayout) {
        const { content: _content, ...rest } = c;
        void _content;
        newCells.push({ ...rest, content: undefined });
      }
      // 否则整格丢弃
      continue;
    }
    newCells.push(c);
  }

  const afterBindings = newCells.filter((c) => isDetailBinding(c.content)).length;

  // 5. 裁剪列表占位空白行。
  //    列表语义 = 锚行绑定 + expand:"down"，其余行由引擎按数据展开生成，模板里的重复数据行
  //    无需在 IR 中保留占位。两类候选删除行：
  //       a) dedupe 识别出的列表区域中，锚行之外的空白行（行内无任何有效内容，含仅剩版式格）；
  //       b) 已按「锚行绑定 + expand:"down"」成型的 IR 中，expand 锚行向下连续的空白行
  //          （这类 IR 的重复数据行已被清空/删除，仅 rows/rowCount 残留大量占位）。
  //    行内仍有文本/分组/主子表条件等内容的行、被 rowspan 合并覆盖的行一律保留；
  //    区域外的手工留白行不受影响。删除后 rows / rowCount / cells 同步收缩、后续行号上移。
  const rowsWithContent = new Set<number>();
  for (const c of newCells) {
    if (hasEffectiveContent(c.content)) rowsWithContent.add(c.row);
  }

  // 合并单元格（rowspan>1）覆盖的行不裁剪，避免破坏跨行合并
  const spannedRows = new Set<number>();
  for (const c of newCells) {
    const span = c.rowspan ?? 1;
    if (span > 1) {
      for (let r = c.row; r < c.row + span; r++) spannedRows.add(r);
    }
  }

  const candidateRows = new Set<number>();
  for (const region of listRegionsInfo) {
    for (let r = region.start + 1; r <= region.end; r++) candidateRows.add(r);
  }
  // expand:"down" 锚行之后连续空白（无有效内容、无 rowspan 覆盖）的行视为列表占位
  for (const c of newCells) {
    if (c.expand !== 'down' || !isDetailBinding(c.content)) continue;
    for (let r = c.row + 1; r <= ir.rowCount; r++) {
      if (rowsWithContent.has(r) || spannedRows.has(r)) break;
      candidateRows.add(r);
    }
  }

  const deleteRows = new Set<number>();
  for (const r of candidateRows) {
    if (!rowsWithContent.has(r) && !spannedRows.has(r)) deleteRows.add(r);
  }

  let trimmedCells = newCells;
  let trimmedRows = ir.rows;
  let trimmedRowCount = ir.rowCount;
  let removedRows = 0;
  if (deleteRows.size > 0) {
    const sortedDel = [...deleteRows].sort((a, b) => a - b);
    // 新行号 = 原行号 - 位于其前的删除行数（二分查找）
    const shift = (row: number): number => {
      let lo = 0;
      let hi = sortedDel.length - 1;
      let less = 0;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (sortedDel[mid]! < row) {
          less = mid + 1;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      return row - less;
    };
    trimmedCells = newCells
      .filter((c) => !deleteRows.has(c.row))
      .map((c) => {
        const newRow = shift(c.row);
        if (newRow === c.row) return c;
        const name = c.name ? shiftCellName(c.name, newRow) : c.name;
        return { ...c, row: newRow, name };
      });
    trimmedRows = ir.rows
      ? ir.rows
          .filter((r) => !deleteRows.has(r.num))
          .map((r) => {
            const newNum = shift(r.num);
            if (newNum === r.num) return r;
            return { ...r, num: newNum };
          })
      : undefined;
    trimmedRowCount = ir.rowCount - deleteRows.size;
    removedRows = deleteRows.size;
  }

  if (stats) {
    stats.removedBindings = removedBindings;
    stats.listRegions = listRegions;
    stats.beforeBindings = beforeBindings;
    stats.afterBindings = afterBindings;
    stats.removedRows = removedRows;
  }

  return { ...ir, cells: trimmedCells, rows: trimmedRows, rowCount: trimmedRowCount };
}
